/**
 * WelderLog - CRUD interface for managing welder numbers
 * 
 * Displayed when user selects "Welder Log" from the piping dropdown.
 * Columns: Employee Code, Employee Name, Welder Number, Shop/Field
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../../utils/apiClient';
import PipingTrackerDropdown from '../../Matrix/PipingTrackerDropdown';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';

const WelderLog = ({ projectId, projectDetails, permissions, pipingSubViewId, onPipingSubViewChange }) => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('welderLog', projectId || 'global', true);
  const [loading, setLoading] = useState(true);
  const [welders, setWelders] = useState([]);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newWelder, setNewWelder] = useState({ empl_code: '', empl_name: '', welder_number: '', shop_field: 'field' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  const fetchWelders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(instance, accounts, 'GET', `/welder-log/project/${projectId}`);
      if (response.data.success) {
        setWelders(response.data.data.rows || []);
      }
    } catch (err) {
      setError('Failed to fetch welders');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, projectId]);

  useEffect(() => {
    fetchWelders();
  }, [fetchWelders]);

  useEffect(() => {
    if (successMsg) {
      const timer = setTimeout(() => setSuccessMsg(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMsg]);

  const handleSave = async (welder) => {
    try {
      setSaving(true);
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/welder-log/save', {
        projectId,
        welder,
      });
      if (response.data.success) {
        setEditingId(null);
        setShowAddForm(false);
        setNewWelder({ empl_code: '', empl_name: '', welder_number: '', shop_field: 'field' });
        setSuccessMsg('Welder saved successfully');
        fetchWelders();
      }
    } catch (err) {
      setError(err.message || 'Failed to save welder');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this welder?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/welder-log/${id}?projectId=${projectId}`);
      setSuccessMsg('Welder deleted');
      fetchWelders();
    } catch (err) {
      setError('Failed to delete welder');
    }
  };

  const filtered = welders.filter(w => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (w.empl_code || '').toLowerCase().includes(s) ||
           (w.empl_name || '').toLowerCase().includes(s) ||
           (w.welder_number || '').toLowerCase().includes(s) ||
           (w.shop_field || '').toLowerCase().includes(s);
  });

  const inputStyle = {
    padding: '6px 10px', borderRadius: '4px', border: '1px solid #d1d5db',
    fontSize: '13px', width: '100%',
  };

  const btnStyle = (bg, color = 'white') => ({
    padding: '5px 12px', backgroundColor: bg, color, border: 'none',
    borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: '500',
  });

  return (
    <div>
      {/* Toolbar */}
      <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search welders..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ padding: '6px 12px', paddingLeft: '30px', borderRadius: '4px', border: '1px solid #ced4da', fontSize: '14px', width: '250px' }}
            />
            <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}>🔍</span>
          </div>
          <span style={{ fontSize: '14px', color: '#666' }}>
            {filtered.length} of {welders.length} welders
          </span>
          
          {onPipingSubViewChange && (
            <PipingTrackerDropdown
              currentSubViewId={pipingSubViewId || 'welder-log'}
              onSubViewChange={onPipingSubViewChange}
              disabled={false}
            />
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            style={{
              padding: '8px 16px', backgroundColor: '#16a34a', color: 'white',
              border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500',
            }}
          >
            {showAddForm ? '- Cancel' : '+ Add Welder'}
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div style={{ padding: '8px 16px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>
          {error}
          <button onClick={() => setError(null)} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>&times;</button>
        </div>
      )}
      {successMsg && (
        <div style={{ padding: '8px 16px', backgroundColor: '#d1fae5', color: '#065f46', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>
          {successMsg}
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <div style={{ padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e5e7eb', marginBottom: '16px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: '600' }}>Add New Welder</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: '12px', alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>Employee Code</label>
              <input value={newWelder.empl_code} onChange={(e) => setNewWelder(p => ({ ...p, empl_code: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>Employee Name</label>
              <input value={newWelder.empl_name} onChange={(e) => setNewWelder(p => ({ ...p, empl_name: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>Welder Number *</label>
              <input value={newWelder.welder_number} onChange={(e) => setNewWelder(p => ({ ...p, welder_number: e.target.value }))} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151', display: 'block', marginBottom: '4px' }}>Shop/Field</label>
              <select value={newWelder.shop_field} onChange={(e) => setNewWelder(p => ({ ...p, shop_field: e.target.value }))} style={inputStyle}>
                <option value="field">Field</option>
                <option value="shop">Shop</option>
              </select>
            </div>
            <button onClick={() => handleSave(newWelder)} disabled={!newWelder.welder_number || saving} style={btnStyle(!newWelder.welder_number || saving ? '#93c5fd' : '#2563eb')}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading welders...</div>
      ) : (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ backgroundColor: '#1e3a5f', color: 'white' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600' }}>Empl Code</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600' }}>Empl Name</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600' }}>Welder Number</th>
                <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: '600' }}>Shop/Field</th>
                <th style={{ padding: '10px 16px', textAlign: 'center', fontWeight: '600', width: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '30px', color: '#6b7280' }}>
                    {search ? 'No welders match your search.' : 'No welders added yet. Click "Add Welder" to get started.'}
                  </td>
                </tr>
              ) : (
                filtered.map((w, idx) => (
                  <tr key={w.id} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    {editingId === w.id ? (
                      <>
                        <td style={{ padding: '6px 16px' }}><input value={editForm.empl_code} onChange={(e) => setEditForm(p => ({ ...p, empl_code: e.target.value }))} style={inputStyle} /></td>
                        <td style={{ padding: '6px 16px' }}><input value={editForm.empl_name} onChange={(e) => setEditForm(p => ({ ...p, empl_name: e.target.value }))} style={inputStyle} /></td>
                        <td style={{ padding: '6px 16px' }}><input value={editForm.welder_number} onChange={(e) => setEditForm(p => ({ ...p, welder_number: e.target.value }))} style={inputStyle} /></td>
                        <td style={{ padding: '6px 16px', textAlign: 'center' }}>
                          <select value={editForm.shop_field} onChange={(e) => setEditForm(p => ({ ...p, shop_field: e.target.value }))} style={inputStyle}>
                            <option value="field">Field</option>
                            <option value="shop">Shop</option>
                          </select>
                        </td>
                        <td style={{ padding: '6px 16px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <button onClick={() => handleSave(editForm)} disabled={saving} style={btnStyle('#16a34a')}>Save</button>
                            <button onClick={() => setEditingId(null)} style={btnStyle('#6b7280')}>Cancel</button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td style={{ padding: '10px 16px' }}>{w.empl_code || '-'}</td>
                        <td style={{ padding: '10px 16px' }}>{w.empl_name || '-'}</td>
                        <td style={{ padding: '10px 16px', fontWeight: '600' }}>{w.welder_number}</td>
                        <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                          <span style={{
                            padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
                            backgroundColor: w.shop_field === 'shop' ? '#fef3c7' : '#dbeafe',
                            color: w.shop_field === 'shop' ? '#92400e' : '#1e40af',
                          }}>
                            {w.shop_field || 'field'}
                          </span>
                        </td>
                        <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <button onClick={() => { setEditingId(w.id); setEditForm({ ...w }); }} style={btnStyle('#3b82f6')}>Edit</button>
                            <button onClick={() => handleDelete(w.id)} style={btnStyle('#ef4444')}>Delete</button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WelderLog;
